import React from "react";
import { Add, CancelOutlined, ClearAll, CropFree, Delete, MyLocation } from "@mui/icons-material";
import { Alert, Box, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";

import { AffineMatrix } from "../types";
import { useGeoReferencer } from "../useGeoReferencer";

const formatCoord = (val: number | null, decimals = 6): string => {
  if (val == null) return "--";
  return val.toFixed(decimals);
};

const formatPixel = (val: number, hasMapPoint: boolean): string => {
  if (val === 0 && !hasMapPoint) return "--";
  return Math.round(val).toString();
};

const getStatusText = (completeGcpCount: number, transformMatrix: AffineMatrix | null, residualError: number | null): string => {
  if (completeGcpCount < 3) {
    return `${completeGcpCount}/3 GCPs complete (need at least 3)`;
  }
  if (transformMatrix) {
    return `Georeferenced (${completeGcpCount} GCPs${residualError != null ? `, RMSE: ${residualError.toFixed(8)}` : ""})`;
  }
  return "GCPs may be collinear - add a non-collinear point";
};

const GCPSidebar: React.FC = () => {
  const {
    gcps,
    activeGcpId,
    mode,
    completeGcpCount,
    residualError,
    transformMatrix,
    addGcp,
    removeGcp,
    startPlacingImage,
    startPlacingMap,
    cancelPlacing,
    clearAll,
  } = useGeoReferencer();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" px={1} py={0.5}>
        <Typography variant="subtitle2">Ground Control Points</Typography>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Add GCP">
            <IconButton size="small" onClick={addGcp} disabled={mode !== "idle"}>
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear all">
            <IconButton size="small" onClick={clearAll}>
              <ClearAll fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Mode indicator */}
      {mode !== "idle" && (
        <Box px={1} pb={0.5}>
          <Alert
            severity="info"
            sx={{ py: 0 }}
            action={
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={cancelPlacing}>
                  <CancelOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            {mode === "place-image" ? "Click on the image to place point" : "Click on the map to place point"}
          </Alert>
        </Box>
      )}

      {/* GCP Table */}
      <TableContainer sx={{ flex: 1, overflow: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 0.5, px: 1, width: 60 }}>Label</TableCell>
              <TableCell sx={{ py: 0.5, px: 1, width: 50 }} align="right">
                X
              </TableCell>
              <TableCell sx={{ py: 0.5, px: 1, width: 50 }} align="right">
                Y
              </TableCell>
              <TableCell sx={{ py: 0.5, px: 1, width: 80 }} align="right">
                Lng
              </TableCell>
              <TableCell sx={{ py: 0.5, px: 1, width: 80 }} align="right">
                Lat
              </TableCell>
              <TableCell sx={{ py: 0.5, px: 1, width: 90 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gcps.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    Click + to add a GCP
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {gcps.map((gcp) => {
              const isActive = gcp.id === activeGcpId;
              const hasImagePoint = gcp.imageX !== 0 || gcp.imageY !== 0 || gcp.mapLng != null;
              return (
                <TableRow key={gcp.id} selected={isActive} hover>
                  <TableCell sx={{ py: 0.25, px: 1, fontSize: "0.75rem" }}>{gcp.label}</TableCell>
                  <TableCell sx={{ py: 0.25, px: 1, fontSize: "0.75rem" }} align="right">
                    {formatPixel(gcp.imageX, gcp.mapLng != null)}
                  </TableCell>
                  <TableCell sx={{ py: 0.25, px: 1, fontSize: "0.75rem" }} align="right">
                    {formatPixel(gcp.imageY, gcp.mapLng != null)}
                  </TableCell>
                  <TableCell sx={{ py: 0.25, px: 1, fontSize: "0.75rem" }} align="right">
                    {formatCoord(gcp.mapLng)}
                  </TableCell>
                  <TableCell sx={{ py: 0.25, px: 1, fontSize: "0.75rem" }} align="right">
                    {formatCoord(gcp.mapLat)}
                  </TableCell>
                  <TableCell sx={{ py: 0.25, px: 1 }} align="center">
                    <Stack direction="row" spacing={0} justifyContent="center">
                      <Tooltip title="Set image point">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => startPlacingImage(gcp.id)}
                            disabled={mode !== "idle"}
                            color={isActive && mode === "place-image" ? "primary" : "default"}
                            sx={{ p: 0.25 }}
                          >
                            <CropFree sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Set map point">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => startPlacingMap(gcp.id)}
                            disabled={mode !== "idle" || !hasImagePoint}
                            color={isActive && mode === "place-map" ? "primary" : "default"}
                            sx={{ p: 0.25 }}
                          >
                            <MyLocation sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete GCP">
                        <IconButton size="small" onClick={() => removeGcp(gcp.id)} sx={{ p: 0.25 }}>
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Status */}
      <Box px={1} py={0.5} borderTop="1px solid" borderColor="divider">
        <Typography variant="caption" color="text.secondary" display="block">
          {getStatusText(completeGcpCount, transformMatrix, residualError)}
        </Typography>
      </Box>
    </Box>
  );
};

export default GCPSidebar;
